import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Prioritaeten in derselben Reihenfolge wie im Backend (app/briefing/abgleich.py).
// Der Server liefert die Karten bereits sortiert -- hier wird NICHT umsortiert,
// sonst laufen zwei Sortierungen auseinander.
const PRIO = {
  dringend: { farbe: '#dc2626', bg: '#fde8e8', label: 'Dringend' },
  hoch:     { farbe: '#ea580c', bg: '#fff4e6', label: 'Hoch' },
  normal:   { farbe: 'var(--brand-blue)', bg: 'var(--brand-bg-tertiary)', label: 'Normal' },
  niedrig:  { farbe: '#6b7280', bg: '#f3f4f6', label: 'Niedrig' },
};

const FAKT_LABEL = { objekt: 'Objekt', vorgang: 'Vorgang', kontakt: 'Kontakt' };

function FaktZeile({ fakt }) {
  // Eine Zeile statt Label-Spalte plus Wert: bei 15 Karten war die Spalte der
  // groesste Anteil am optischen Rauschen (Jonas 2026-07-29: "unuebersichtlich").
  // Die Quelle bleibt sichtbar, aber gedimmt -- ein Fakt ohne nachvollziehbare
  // Herkunft ist genau das, was dieses Briefing nicht sein soll.
  return (
    <div style={{ fontSize: 12, lineHeight: 1.6, color: 'var(--brand-navy)' }}>
      <span style={{ color: 'var(--brand-navy-70)', marginRight: 6 }}>
        {FAKT_LABEL[fakt.art] || fakt.art}
      </span>
      {fakt.wert}
      <span
        title={`Quelle: ${fakt.quelle}`}
        style={{ color: 'var(--brand-navy-70)', fontFamily: 'var(--font-mono)', fontSize: 10, marginLeft: 6, opacity: 0.7 }}
      >
        {fakt.quelle}
      </span>
    </div>
  );
}

// Feldnamen der Skills sind technisch (objekt_ids). In der Rueckfrage steht das,
// was der Nutzer vor dem Start liest -- also Klartext.
const FELD_LABEL = {
  adresse: 'Adresse',
  beschreibung: 'Inhalt',
  objekt_ids: 'Objekt-Nr.',
  objekt_nr: 'Objekt-Nr.',
};

const CHIP_BASIS = {
  fontSize: 11, padding: '6px 12px', borderRadius: 9999,
  border: '1px solid var(--brand-warm-gray)', background: '#fff',
  color: 'var(--brand-navy)', cursor: 'pointer', textDecoration: 'none',
  display: 'inline-block',
};

// Ausgefuehrt wird ueber POST /api/skills/<id>/execute -- denselben Endpunkt, den
// die Skill-Seite nutzt und der die Rollenpruefung macht. Kein zweiter Pfad: der
// Chip ist eine vorbefuellte Abkuerzung auf einen Weg, der ohnehin offen steht.
function AutomationChip({ automation: a }) {
  const [zustand, setZustand] = useState('chip'); // chip | frage | laeuft | entwurf | fertig | fehler
  const [entwurf, setEntwurf] = useState(null);
  const [betreff, setBetreff] = useState('');
  const [meldung, setMeldung] = useState('');
  const [dateien, setDateien] = useState([]);
  const editorRef = useRef(null);

  const post = async (nutzlast) => {
    const fd = new FormData();
    fd.append('data', JSON.stringify(nutzlast));
    const r = await fetch(`${API_URL}/skills/${a.skill_id}/execute`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('fleet_token')}` },
      body: fd,
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
      throw new Error(body.detail?.errors?.join(', ') || body.detail || `Fehler ${r.status}`);
    }
    return body.result || {};
  };

  const starten = async () => {
    setZustand('laeuft');
    try {
      // Erster Aufruf: ausschliesslich die Vorbefuellung aus dem Katalog. Nie _phase
      // (die kommt nur aus dem zweiten, bestaetigten Schritt unten) und beim Aushang
      // nie betreff+inhalt zusammen -- diese Kombination ueberspringt die
      // Entwurfsstufe und erzeugt sofort das PDF.
      const res = await post(a.vorbefuellung);
      if (res.phase === 'preview') {
        setBetreff(res.betreff || '');
        setEntwurf(res);
        setZustand('entwurf');
      } else {
        setDateien(res.files || []);
        setMeldung(res.summary || (res.job_id ? 'Gestartet, läuft im Hintergrund.' : 'Fertig.'));
        setZustand('fertig');
      }
    } catch (e) {
      setMeldung(e.message);
      setZustand('fehler');
    }
  };

  const erzeugen = async () => {
    setZustand('laeuft');
    try {
      const res = await post({
        _phase: 'generate_pdf',
        betreff,
        inhalt_html: editorRef.current ? editorRef.current.innerHTML : entwurf.inhalt_html,
        adresse: entwurf.adresse,
        objekt: entwurf.objekt,
        datum: entwurf.datum,
      });
      setDateien(res.files || []);
      setMeldung(res.summary || 'Fertig.');
      setZustand('fertig');
    } catch (e) {
      setMeldung(e.message);
      setZustand('fehler');
    }
  };

  // Datei-Skills (Mietvertrag, Protokolle) brauchen einen Upload, also fuehrt der
  // Chip auf die Skill-Seite. Vorbefuellen muss man dort nichts: ausser dem
  // Datei-Feld haben sie keine Pflichtfelder.
  if (!a.in_karte_ausloesbar) {
    return (
      <Link to={`/skills/${a.skill_id}`} style={{ ...CHIP_BASIS, borderStyle: 'dashed' }}>
        {a.label}
      </Link>
    );
  }

  if (zustand === 'chip') {
    return (
      <button onClick={() => setZustand('frage')} style={CHIP_BASIS}>{a.label}</button>
    );
  }

  if (zustand === 'laeuft') {
    return (
      <span style={{ ...CHIP_BASIS, cursor: 'default', color: 'var(--brand-navy-70)' }}>
        {a.label} läuft …
      </span>
    );
  }

  const rahmen = {
    width: '100%', border: '1px solid var(--brand-warm-gray)', borderRadius: 6,
    padding: '10px 12px', fontSize: 12, color: 'var(--brand-navy)',
    background: 'var(--brand-bg-tertiary)',
  };

  if (zustand === 'frage') {
    return (
      <div style={rahmen}>
        <strong style={{ fontSize: 12 }}>{a.label}</strong>
        <div style={{ margin: '6px 0 8px' }}>
          {Object.entries(a.vorbefuellung).map(([feld, wert]) => (
            <div key={feld} style={{ lineHeight: 1.5 }}>
              <span style={{ color: 'var(--brand-navy-70)', marginRight: 6 }}>
                {FELD_LABEL[feld] || feld}
              </span>
              {wert}
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--brand-navy-70)', marginBottom: 8 }}>
          {a.mutierend
            ? 'Wird mit echten Daten ausgeführt, kein Testlauf.'
            : 'Nur Abruf, ändert nichts in Immoware24.'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={starten} style={{ ...CHIP_BASIS, borderColor: 'var(--brand-gold)' }}>
            Starten
          </button>
          <button onClick={() => setZustand('chip')} style={{ ...CHIP_BASIS, color: 'var(--brand-navy-70)' }}>
            Abbrechen
          </button>
        </div>
      </div>
    );
  }

  if (zustand === 'entwurf') {
    return (
      <div style={rahmen}>
        <div style={{ fontSize: 11, color: 'var(--brand-navy-70)', marginBottom: 6 }}>
          Entwurf, noch nichts erzeugt. Betreff und Text lassen sich hier ändern.
        </div>
        <input
          value={betreff}
          onChange={e => setBetreff(e.target.value)}
          style={{
            width: '100%', padding: '6px 10px', fontSize: 13, marginBottom: 8,
            border: '1px solid var(--brand-warm-gray)', borderRadius: 4, boxSizing: 'border-box',
          }}
        />
        <div
          contentEditable
          suppressContentEditableWarning
          ref={el => {
            // Einmalig befuellen: bei jedem Render neu zu setzen wuerde den Cursor
            // beim Tippen an den Anfang springen lassen.
            if (el && !el.dataset.gefuellt) {
              el.innerHTML = entwurf.inhalt_html || '';
              el.dataset.gefuellt = '1';
              editorRef.current = el;
            }
          }}
          style={{
            background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 4,
            padding: '8px 10px', fontSize: 12, lineHeight: 1.6, minHeight: 120,
            marginBottom: 8, outline: 'none', overflow: 'auto',
          }}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={erzeugen} style={{ ...CHIP_BASIS, borderColor: 'var(--brand-gold)' }}>
            PDF erzeugen
          </button>
          <button onClick={() => setZustand('chip')} style={{ ...CHIP_BASIS, color: 'var(--brand-navy-70)' }}>
            Verwerfen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      ...rahmen,
      borderColor: zustand === 'fehler' ? 'var(--brand-danger)' : 'var(--brand-warm-gray)',
      background: zustand === 'fehler' ? 'var(--brand-danger-bg)' : 'var(--brand-bg-tertiary)',
    }}>
      <div style={{ marginBottom: dateien.length || zustand === 'fehler' ? 6 : 0 }}>{meldung}</div>
      {dateien.map((f, i) => (
        <div key={i}>
          <a href={f.url} target="_blank" rel="noreferrer" style={{ color: 'var(--brand-blue)' }}>
            {f.name}
          </a>
        </div>
      ))}
      <button onClick={() => setZustand('chip')} style={{ ...CHIP_BASIS, marginTop: 6 }}>
        {zustand === 'fehler' ? 'Erneut versuchen' : 'Schließen'}
      </button>
    </div>
  );
}

function Karte({ karte }) {
  const prio = PRIO[karte.einordnung?.prio] || PRIO.niedrig;
  const v = karte.vorgang;
  const m = karte.mail;

  return (
    <div style={{
      background: '#fff', border: '1px solid var(--brand-warm-gray)',
      borderLeft: `4px solid ${prio.farbe}`, borderRadius: 8,
      padding: '14px 18px', marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        {/* Kein Prio-Badge mehr an der Karte: die Gruppen-Ueberschrift traegt die
            Prioritaet, das Badge war die dritte Wiederholung derselben Information
            (Rahmenfarbe, Ueberschrift, Badge). */}
        <strong style={{ fontSize: 14, color: 'var(--brand-navy)' }}>
          {v ? v.titel : m?.betreff}
        </strong>
        {v && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--brand-navy-70)' }}>
            Nr. {v.ticket_nr} · {v.status}
            {v.faellig_am ? ` · fällig ${v.faellig_am}` : ''}
          </span>
        )}
        {m && (
          <span style={{ fontSize: 11, color: 'var(--brand-navy-70)' }}>
            von {m.von}
            {m.datum ? ` · ${new Date(m.datum).toLocaleString('de-DE', {
              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
            })}` : ''}
            {m.anhang ? ' · Anhang' : ''}
          </span>
        )}
      </div>

      {/* Die Vorschau ist bei Mail-Karten nicht Beiwerk, sondern der Inhalt: vier
          von fünf Mails lassen sich keinem Objekt zuordnen (gemessen 18,8 %
          Bindequote), tragen also weder Fakten noch Entwurf. Ohne diesen Absatz
          bliebe auf so einer Karte nur Betreff und Absender stehen, und die Karte
          wäre wertlos gegenüber dem Postfach selbst. */}
      {m?.vorschau && (
        <p style={{
          margin: '8px 0 0', fontSize: 12, color: 'var(--brand-navy)', lineHeight: 1.5,
          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {m.vorschau}
        </p>
      )}

      <p style={{ margin: '8px 0 10px', fontSize: 12, color: 'var(--brand-navy-70)' }}>
        {karte.einordnung?.begruendung}
      </p>

      {karte.fakten?.length > 0 && (
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 4,
          background: 'var(--brand-bg-tertiary)', borderRadius: 6, padding: '8px 10px',
        }}>
          {karte.fakten.map((f, i) => <FaktZeile key={i} fakt={f} />)}
        </div>
      )}

      {karte.entwurf && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--brand-navy-70)', marginBottom: 4 }}>
            Antwortentwurf
          </div>
          <textarea
            defaultValue={karte.entwurf.text}
            rows={5}
            style={{
              width: '100%', fontSize: 12, fontFamily: 'var(--font-body)', padding: 10,
              border: '1px solid var(--brand-warm-gray)', borderRadius: 6, resize: 'vertical',
            }}
          />
        </div>
      )}

      {karte.automationen?.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {karte.automationen.map((a, i) => <AutomationChip key={i} automation={a} />)}
        </div>
      )}
    </div>
  );
}

// Reihenfolge der Gruppen = Reihenfolge der Dringlichkeit. Die Karten innerhalb
// einer Gruppe behalten die Server-Reihenfolge -- hier wird NICHT umsortiert.
const GRUPPEN = ['dringend', 'hoch', 'normal', 'niedrig'];
const SOFORT_SICHTBAR = ['dringend', 'hoch'];

function Gruppen({ karten }) {
  // Nebenprioritaeten eingeklappt: bei 15 Karten hat man sonst 15 gleich laute
  // Bloecke und sieht nicht, was zuerst drankommt.
  const [offen, setOffen] = useState(false);

  const nachPrio = GRUPPEN.map(p => [p, karten.filter(k => k.einordnung?.prio === p)])
    .filter(([, ks]) => ks.length > 0);
  const wichtig = nachPrio.filter(([p]) => SOFORT_SICHTBAR.includes(p));
  const rest = nachPrio.filter(([p]) => !SOFORT_SICHTBAR.includes(p));
  const restAnzahl = rest.reduce((n, [, ks]) => n + ks.length, 0);

  const kopf = (p, anzahl) => (
    <div key={`h-${p}`} style={{
      display: 'flex', alignItems: 'center', gap: 8, margin: '18px 0 8px',
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
      color: PRIO[p].farbe,
    }}>
      {PRIO[p].label}
      <span style={{ color: 'var(--brand-navy-70)', fontWeight: 500 }}>{anzahl}</span>
      <span style={{ flex: 1, height: 1, background: 'var(--brand-warm-gray)' }} />
    </div>
  );

  return (
    <>
      {wichtig.map(([p, ks]) => (
        <React.Fragment key={p}>
          {kopf(p, ks.length)}
          {ks.map(k => <Karte key={k.index} karte={k} />)}
        </React.Fragment>
      ))}

      {restAnzahl > 0 && (
        <button
          onClick={() => setOffen(o => !o)}
          style={{
            marginTop: 18, fontSize: 12, padding: '8px 16px', borderRadius: 9999,
            border: '1px solid var(--brand-warm-gray)', background: '#fff',
            color: 'var(--brand-navy-70)', cursor: 'pointer', width: '100%',
          }}
        >
          {offen ? 'Weniger dringende ausblenden' : `${restAnzahl} weniger dringende anzeigen`}
        </button>
      )}

      {offen && rest.map(([p, ks]) => (
        <React.Fragment key={p}>
          {kopf(p, ks.length)}
          {ks.map(k => <Karte key={k.index} karte={k} />)}
        </React.Fragment>
      ))}
    </>
  );
}

export default function Briefing() {
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState('');
  const [laedt, setLaedt] = useState(true);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler('');
    try {
      const r = await fetch(`${API_URL}/briefing`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('fleet_token')}` },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `Fehler ${r.status}`);
      }
      setDaten(await r.json());
    } catch (e) {
      setFehler(e.message);
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '8px 0 40px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
        <h1 style={{ fontSize: 22, margin: 0, color: 'var(--brand-navy)' }}>Briefing</h1>
        <button
          onClick={laden}
          disabled={laedt}
          style={{
            fontSize: 11, padding: '6px 14px', borderRadius: 9999, cursor: laedt ? 'default' : 'pointer',
            border: '1px solid var(--brand-warm-gray)', background: '#fff', color: 'var(--brand-navy-70)',
          }}
        >
          {laedt ? 'lädt …' : 'Neu laden'}
        </button>
      </div>

      {daten?.zusammenfassung && (
        <p style={{ fontSize: 13, color: 'var(--brand-navy-70)', margin: '0 0 12px' }}>
          {daten.zusammenfassung}
        </p>
      )}

      {/* Der Hinweis erklaert, WARUM die Liste leer oder unvollstaendig ist. Ohne ihn
          liest sich eine leere Liste als Entwarnung -- genau das soll nicht passieren. */}
      {daten?.hinweis && (
        <div style={{
          background: 'var(--brand-gold-10)', border: '1px solid var(--brand-gold)',
          borderRadius: 6, padding: '10px 14px', fontSize: 12, color: 'var(--brand-navy)',
          marginBottom: 14,
        }}>
          {daten.hinweis}
        </div>
      )}

      {fehler && (
        <div style={{
          background: 'var(--brand-danger-bg)', border: '1px solid var(--brand-danger)',
          borderRadius: 6, padding: '10px 14px', fontSize: 12, marginBottom: 14,
        }}>
          {fehler}
        </div>
      )}

      {laedt && !daten && (
        <p style={{ fontSize: 13, color: 'var(--brand-navy-70)' }}>Briefing wird erstellt …</p>
      )}

      {daten && <Gruppen karten={daten.karten} />}

      {daten && daten.karten.length === 0 && !daten.hinweis && (
        <p style={{ fontSize: 13, color: 'var(--brand-navy-70)' }}>Keine offenen Vorgänge.</p>
      )}
    </div>
  );
}
